import axios from "axios";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import DataTable from "react-data-table-component";
import { getApiBaseUrl } from "../../utils/utils";

export default function MedicalAttendanceGeofence() {
  const API_URL = process.env.REACT_APP_API_URL;
  const [msg, setMsg] = useState("");
  const [errormsg, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [geofences, setGeofences] = useState([]);
  const [partners, setPartners] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { partnerId: initialPartnerId } = location.state || {};

  const [selectedPartner, setSelectedPartner] = useState(initialPartnerId || "");
  const [formData, setFormData] = useState({
    id: null,
    name: "",
    latitude: "",
    longitude: "",
    radiusMeters: 100,
    address: "",
    active: true,
  });

  useEffect(() => {
    fetchPartners();
  }, []);

  useEffect(() => {
    if (selectedPartner) {
      fetchGeofences(selectedPartner);
    } else {
      setGeofences([]);
    }
  }, [selectedPartner]);

  const fetchPartners = async () => {
    try {
      const baseApiUrl = getApiBaseUrl();
      const response = await axios.get(
        `${baseApiUrl}/v1.0/partner/retrieve_list?page=0`
      );
      // Filter for medical partners only
      const allPartners = response.data.data || [];
      const medicalPartners = allPartners.filter(
        (partner) => partner.accountType?.toLowerCase() === "medical"
      );
      setPartners(medicalPartners);
      console.log("Fetched medical partners:", medicalPartners.length);
    } catch (err) {
      console.error("Error fetching partners:", err);
      setError("Failed to load medical partners");
    }
  };

  const fetchGeofences = async (partnerId) => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}shypvdriverapp/api/medical-attendance/geofences/partner/${partnerId}`
      );
      if (response.data.success) {
        setGeofences(response.data.data || []);
      } else {
        setGeofences([]);
      }
    } catch (err) {
      console.error("Error fetching geofences:", err);
      setGeofences([]);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      id: null,
      name: "",
      latitude: "",
      longitude: "",
      radiusMeters: 100,
      address: "",
      active: true,
    });
    setEditMode(false);
  };

  const handleOpenModal = (geo = null) => {
    if (geo) {
      setFormData({
        id: geo.id,
        name: geo.name,
        latitude: geo.latitude,
        longitude: geo.longitude,
        radiusMeters: geo.radiusMeters,
        address: geo.address || "",
        active: geo.active ?? true,
      });
      setEditMode(true);
    } else {
      resetForm();
    }
    setShowModal(true);
  };

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setFormData((prev) => ({
            ...prev,
            latitude: position.coords.latitude.toFixed(6),
            longitude: position.coords.longitude.toFixed(6),
          }));
          setMsg("Location captured successfully");
          setTimeout(() => setMsg(""), 3000);
        },
        (error) => {
          setError("Failed to get current location: " + error.message);
          setTimeout(() => setError(""), 3000);
        }
      );
    } else {
      setError("Geolocation is not supported");
    }
  };

  const handleSubmit = async () => {
    setError("");
    setMsg("");

    if (!formData.name || !formData.latitude || !formData.longitude) {
      setError("Please fill in all required fields");
      return;
    }

    if (!selectedPartner) {
      setError("Please select a partner first");
      return;
    }

    try {
      const payload = {
        name: formData.name,
        latitude: parseFloat(formData.latitude),
        longitude: parseFloat(formData.longitude),
        radiusMeters: parseInt(formData.radiusMeters) || 100,
        address: formData.address,
        partnerId: parseInt(selectedPartner),
        active: formData.active,
      };

      let response;
      if (editMode && formData.id) {
        response = await axios.put(
          `${API_URL}shypvdriverapp/api/medical-attendance/geofences/${formData.id}`,
          payload
        );
      } else {
        response = await axios.post(
          `${API_URL}shypvdriverapp/api/medical-attendance/geofences`,
          payload
        );
      }

      if (response.data.success) {
        setMsg(editMode ? "Location updated successfully" : "Location added successfully");
        fetchGeofences(selectedPartner);
        setShowModal(false);
        resetForm();
      } else {
        setError(response.data.message || "Operation failed");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Error saving location");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this location?")) {
      return;
    }
    try {
      const response = await axios.delete(
        `${API_URL}shypvdriverapp/api/medical-attendance/geofences/${id}`
      );
      if (response.data.success) {
        setMsg("Location deleted successfully");
        fetchGeofences(selectedPartner);
      } else {
        setError(response.data.message || "Failed to delete");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Error deleting location");
    }
  };

  const getPartnerName = (pid) => {
    const partner = partners.find((p) => p.pid === pid);
    return partner ? partner.pname : `Partner ${pid}`;
  };

  const columns = [
    {
      name: "#",
      selector: (row, index) => index + 1,
      width: "60px",
    },
    {
      name: "Location Name",
      selector: (row) => row.name,
      sortable: true,
      width: "180px",
      cell: (row) => (
        <div>
          <div style={{ fontWeight: 500 }}>{row.name}</div>
          {row.address && (
            <div style={{ fontSize: "11px", color: "#666" }}>{row.address}</div>
          )}
        </div>
      ),
    },
    {
      name: "Coordinates",
      selector: (row) => `${row.latitude}, ${row.longitude}`,
      sortable: false,
      width: "200px",
      cell: (row) => (
        <div style={{ fontSize: "13px" }}>
          <div>
            <i className="fa fa-map-marker" style={{ color: "#dc3545", marginRight: "5px" }}></i>
            {parseFloat(row.latitude).toFixed(6)}, {parseFloat(row.longitude).toFixed(6)}
          </div>
          <a
            href={`https://www.google.com/maps?q=${row.latitude},${row.longitude}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontSize: "11px", color: "#007bff" }}
          >
            View on Map
          </a>
        </div>
      ),
    },
    {
      name: "Radius",
      selector: (row) => row.radiusMeters,
      sortable: true,
      width: "100px",
      cell: (row) => (
        <span
          style={{
            padding: "4px 10px",
            backgroundColor: "#e3f2fd",
            borderRadius: "15px",
            fontSize: "12px",
            color: "#1565c0",
          }}
        >
          {row.radiusMeters}m
        </span>
      ),
    },
    {
      name: "Status",
      selector: (row) => row.active,
      sortable: true,
      width: "100px",
      cell: (row) => (
        <span
          style={{
            padding: "4px 10px",
            borderRadius: "15px",
            fontSize: "12px",
            backgroundColor: row.active !== false ? "#d4edda" : "#f8d7da",
            color: row.active !== false ? "#155724" : "#721c24",
          }}
        >
          {row.active !== false ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      name: "Actions",
      width: "160px",
      cell: (row) => (
        <div style={{ display: "flex", gap: "8px" }}>
          <button
            onClick={() => handleOpenModal(row)}
            style={{
              backgroundColor: "#007bff",
              color: "#fff",
              border: "none",
              borderRadius: "4px",
              padding: "6px 10px",
              cursor: "pointer",
              fontSize: "12px",
            }}
          >
            <i className="fa fa-edit"></i> Edit
          </button>
          <button
            onClick={() => handleDelete(row.id)}
            style={{
              backgroundColor: "#dc3545",
              color: "#fff",
              border: "none",
              borderRadius: "4px",
              padding: "6px 10px",
              cursor: "pointer",
              fontSize: "12px",
            }}
          >
            <i className="fa fa-trash"></i>
          </button>
        </div>
      ),
      ignoreRowClick: true,
    },
  ];

  const inputStyle = {
    width: "100%",
    padding: "10px 12px",
    borderRadius: "6px",
    border: "1px solid #ddd",
    fontSize: "14px",
    marginTop: "6px",
  };

  const labelStyle = {
    fontWeight: "500",
    color: "#333",
    marginBottom: "5px",
    display: "block",
  };

  const customStyles = {
    headRow: {
      style: {
        backgroundColor: "#f8f9fa",
        borderBottom: "2px solid #dee2e6",
      },
    },
    headCells: {
      style: {
        fontWeight: "600",
        fontSize: "13px",
        color: "#495057",
      },
    },
    rows: {
      style: {
        minHeight: "65px",
      },
    },
  };

  return (
    <div>
      <div className="form-container">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px",
          }}
        >
          <h5 style={{ margin: 0, color: "#333" }}>
            <i className="fa fa-map-marker" style={{ marginRight: "10px" }}></i>
            Geofence / Location Management
          </h5>
          <button
            onClick={() => navigate("/orders/medical-attendance-config")}
            style={{
              backgroundColor: "#6c757d",
              color: "#fff",
              border: "none",
              borderRadius: "6px",
              padding: "8px 16px",
              cursor: "pointer",
              fontSize: "14px",
            }}
          >
            <i className="fa fa-arrow-left" style={{ marginRight: "8px" }}></i>
            Back
          </button>
        </div>

        {msg && (
          <div
            style={{
              padding: "12px 15px",
              marginBottom: "15px",
              backgroundColor: "#d4edda",
              color: "#155724",
              borderRadius: "6px",
              border: "1px solid #c3e6cb",
            }}
          >
            <i className="fa fa-check-circle" style={{ marginRight: "8px" }}></i>
            {msg}
          </div>
        )}

        {errormsg && (
          <div
            style={{
              padding: "12px 15px",
              marginBottom: "15px",
              backgroundColor: "#f8d7da",
              color: "#721c24",
              borderRadius: "6px",
              border: "1px solid #f5c6cb",
            }}
          >
            <i className="fa fa-exclamation-circle" style={{ marginRight: "8px" }}></i>
            {errormsg}
          </div>
        )}

        {/* Partner Selection and Add Button */}
        <div
          style={{
            backgroundColor: "#fff",
            borderRadius: "10px",
            padding: "20px",
            marginBottom: "20px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          }}
        >
          <div style={{ display: "flex", gap: "15px", alignItems: "flex-end", flexWrap: "wrap" }}>
            <div style={{ flex: "1", minWidth: "250px" }}>
              <label style={labelStyle}>Select Partner</label>
              <select
                value={selectedPartner}
                onChange={(e) => setSelectedPartner(e.target.value)}
                style={{
                  ...inputStyle,
                  backgroundColor: "#fff",
                  cursor: "pointer",
                }}
              >
                <option value="">-- Select a Partner --</option>
                {partners
                  .sort((a, b) => a.pname.localeCompare(b.pname))
                  .map((p) => (
                    <option key={p.pid} value={p.pid}>
                      {p.pname}
                    </option>
                  ))}
              </select>
            </div>
            <button
              onClick={() => handleOpenModal()}
              disabled={!selectedPartner}
              style={{
                backgroundColor: selectedPartner ? "#28a745" : "#ccc",
                color: "#fff",
                border: "none",
                borderRadius: "6px",
                padding: "12px 20px",
                cursor: selectedPartner ? "pointer" : "not-allowed",
                fontSize: "14px",
                fontWeight: "500",
                height: "46px",
              }}
            >
              <i className="fa fa-plus" style={{ marginRight: "8px" }}></i>
              Add New Location
            </button>
          </div>
        </div>

        {/* Locations Table */}
        <div
          style={{
            backgroundColor: "#fff",
            borderRadius: "10px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
            overflow: "hidden",
          }}
        >
          {!selectedPartner ? (
            <div style={{ padding: "50px", textAlign: "center" }}>
              <i
                className="fa fa-hand-pointer-o"
                style={{ fontSize: "48px", color: "#ccc", marginBottom: "15px" }}
              ></i>
              <h5 style={{ color: "#666", marginBottom: "10px" }}>
                Select a Partner
              </h5>
              <p style={{ color: "#999" }}>
                Choose a partner from the dropdown to manage their locations
              </p>
            </div>
          ) : loading ? (
            <div style={{ padding: "50px", textAlign: "center" }}>
              <i
                className="fa fa-spinner fa-spin"
                style={{ fontSize: "32px", color: "#007bff" }}
              ></i>
              <p style={{ marginTop: "15px", color: "#666" }}>Loading locations...</p>
            </div>
          ) : geofences.length === 0 ? (
            <div style={{ padding: "50px", textAlign: "center" }}>
              <i
                className="fa fa-map-marker"
                style={{ fontSize: "48px", color: "#ffc107", marginBottom: "15px" }}
              ></i>
              <h5 style={{ color: "#666", marginBottom: "10px" }}>
                No Locations Found
              </h5>
              <p style={{ color: "#999", marginBottom: "20px" }}>
                Add locations where drivers can check in/out for{" "}
                <strong>{getPartnerName(parseInt(selectedPartner))}</strong>
              </p>
              <button
                onClick={() => handleOpenModal()}
                style={{
                  backgroundColor: "#28a745",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  padding: "12px 24px",
                  cursor: "pointer",
                  fontSize: "14px",
                }}
              >
                <i className="fa fa-plus" style={{ marginRight: "8px" }}></i>
                Add First Location
              </button>
            </div>
          ) : (
            <DataTable
              columns={columns}
              data={geofences}
              pagination
              highlightOnHover
              striped
              responsive
              customStyles={customStyles}
            />
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
          onClick={() => setShowModal(false)}
        >
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "12px",
              padding: "25px",
              width: "90%",
              maxWidth: "500px",
              boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
              maxHeight: "90vh",
              overflowY: "auto",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h5 style={{ marginBottom: "20px", color: "#333" }}>
              <i className="fa fa-map-marker" style={{ marginRight: "10px" }}></i>
              {editMode ? "Edit Location" : "Add New Location"}
            </h5>

            <div style={{ marginBottom: "15px" }}>
              <label style={labelStyle}>Location Name *</label>
              <input
                type="text"
                placeholder="e.g., Main Office, Warehouse A"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                style={inputStyle}
              />
            </div>

            <div style={{ marginBottom: "15px" }}>
              <label style={labelStyle}>Address (Optional)</label>
              <input
                type="text"
                placeholder="Full street address"
                value={formData.address}
                onChange={(e) =>
                  setFormData({ ...formData, address: e.target.value })
                }
                style={inputStyle}
              />
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "15px",
                marginBottom: "15px",
              }}
            >
              <div>
                <label style={labelStyle}>Latitude *</label>
                <input
                  type="number"
                  step="0.000001"
                  placeholder="e.g., 25.2048"
                  value={formData.latitude}
                  onChange={(e) =>
                    setFormData({ ...formData, latitude: e.target.value })
                  }
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>Longitude *</label>
                <input
                  type="number"
                  step="0.000001"
                  placeholder="e.g., 55.2708"
                  value={formData.longitude}
                  onChange={(e) =>
                    setFormData({ ...formData, longitude: e.target.value })
                  }
                  style={inputStyle}
                />
              </div>
            </div>

            <button
              onClick={getCurrentLocation}
              style={{
                backgroundColor: "#17a2b8",
                color: "#fff",
                border: "none",
                borderRadius: "6px",
                padding: "12px 16px",
                cursor: "pointer",
                width: "100%",
                fontSize: "14px",
                marginBottom: "15px",
              }}
            >
              <i className="fa fa-crosshairs" style={{ marginRight: "8px" }}></i>
              Use Current Location
            </button>

            <div style={{ marginBottom: "15px" }}>
              <label style={labelStyle}>Radius (meters)</label>
              <input
                type="number"
                min="10"
                max="1000"
                placeholder="Default: 100"
                value={formData.radiusMeters}
                onChange={(e) =>
                  setFormData({ ...formData, radiusMeters: e.target.value })
                }
                style={inputStyle}
              />
              <small style={{ color: "#666", marginTop: "5px", display: "block" }}>
                Drivers must be within this radius to check in/out (10-1000m)
              </small>
            </div>

            <div
              style={{
                marginBottom: "20px",
                padding: "12px",
                backgroundColor: "#f8f9fa",
                borderRadius: "6px",
              }}
            >
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={formData.active}
                  onChange={(e) =>
                    setFormData({ ...formData, active: e.target.checked })
                  }
                  style={{ marginRight: "10px", width: "18px", height: "18px" }}
                />
                <span style={{ fontWeight: "500" }}>Location Active</span>
              </label>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
              <button
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
                style={{
                  backgroundColor: "#6c757d",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  padding: "10px 20px",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                style={{
                  backgroundColor: "#28a745",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  padding: "10px 20px",
                  cursor: "pointer",
                }}
              >
                <i
                  className={`fa ${editMode ? "fa-save" : "fa-plus"}`}
                  style={{ marginRight: "6px" }}
                ></i>
                {editMode ? "Update Location" : "Add Location"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
